/**
 * Request Info Hooks
 *
 * Hooks to access request information from anywhere in the app.
 * The data comes from the root loader via useRouteLoaderData.
 */

import { useEffect } from 'react';
import { useRevalidator, useRouteLoaderData } from 'react-router';

import {
  colorSchemeHint,
  getHintUtils,
  subscribeToSchemeChange,
  timeZoneHint,
} from './client-hints';
import type { RequestInfo } from './types';

// Initialize client hints utils for the check script
const hintsUtils = getHintUtils({
  colorScheme: colorSchemeHint,
  timeZone: timeZoneHint,
});

/**
 * Type for the root loader data
 * This should match what root.tsx returns
 */
interface RootLoaderData {
  requestInfo: RequestInfo;
}

/**
 * Get request info from root loader
 *
 * @throws Error if not used within a route that has root loader data
 */
export function useRequestInfo(): RequestInfo {
  const data = useRouteLoaderData('root') as RootLoaderData | undefined;
  if (!data?.requestInfo) {
    throw new Error(
      'useRequestInfo must be used within a route that has root loader data',
    );
  }
  return data.requestInfo;
}

/**
 * Get request info from root loader (optional version)
 *
 * Returns undefined if not within a route with root loader data
 */
export function useOptionalRequestInfo(): RequestInfo | undefined {
  const data = useRouteLoaderData('root') as RootLoaderData | undefined;
  return data?.requestInfo;
}

/**
 * Get just the hints from request info
 */
export function useHints() {
  const requestInfo = useRequestInfo();
  return requestInfo.hints;
}

/**
 * Get just the hints from request info (optional version)
 */
export function useOptionalHints() {
  const requestInfo = useOptionalRequestInfo();
  return requestInfo?.hints;
}

/**
 * Get just the user preferences from request info
 */
export function useUserPrefs() {
  const requestInfo = useRequestInfo();
  return requestInfo.userPrefs;
}

/**
 * Get just the auth info from request info
 */
export function useAuth() {
  const requestInfo = useRequestInfo();
  return requestInfo.auth;
}

/**
 * Get just the auth info from request info (optional version)
 */
export function useOptionalAuth() {
  const requestInfo = useOptionalRequestInfo();
  return requestInfo?.auth;
}

interface ClientHintCheckProps {
  /**
   * Nonce for CSP (Content Security Policy)
   * If not provided, script will work but may be blocked by strict CSP
   */
  nonce?: string;
}

/**
 * Component that checks client hints and reloads if they've changed
 *
 * This should be placed in the <head> of the document to run
 * before the page is visible to the user.
 *
 * It also subscribes to color scheme changes and triggers a revalidation
 * when the user changes their system preference.
 */
export function ClientHintCheck({ nonce }: ClientHintCheckProps) {
  const { revalidate } = useRevalidator();

  useEffect(() => {
    return subscribeToSchemeChange(() => revalidate());
  }, [revalidate]);

  return (
    <script
      nonce={nonce}
      dangerouslySetInnerHTML={{
        __html: hintsUtils.getClientHintCheckScript(),
      }}
    />
  );
}
